import HtmlDiff from "htmldiff-js";
import {
  decodeDanishCharsFromHtmlDiff,
  encodeDanishCharsForHtmlDiff,
} from "./danishDiffEncoding";
import { logger } from "@/lib/logger";

const executeHtmlDiff = (oldHtml: string, newHtml: string): string => {
  const encodedOld = encodeDanishCharsForHtmlDiff(oldHtml);
  const encodedNew = encodeDanishCharsForHtmlDiff(newHtml);
  return decodeDanishCharsFromHtmlDiff(
    HtmlDiff.execute(encodedOld, encodedNew)
  );
};

/**
 * Computes an inline HTML diff between two HTML strings using a background
 * Web Worker to avoid blocking the main thread on large legislation documents.
 *
 * Falls back to synchronous computation if workers are unavailable or if the
 * worker fails to load.
 *
 * The returned HTML contains:
 * - <ins class="diffins"> elements for added content
 * - <del class="diffdel"> elements for removed content
 * - <del class="diffmod"> / <ins class="diffmod"> pairs for modified content
 */
export const computeHtmlDiff = (
  oldHtml: string,
  newHtml: string
): Promise<string> => {
  return new Promise((resolve, reject) => {
    let worker: Worker | null = null;
    let settled = false;

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      worker?.terminate();
      fn();
    };

    try {
      // Vite bundles the worker as a module when using `new URL(..., import.meta.url)`
      worker = new Worker(
        new URL("./htmlDiffWorker.ts", import.meta.url),
        { type: "module" }
      );

      worker.onmessage = (
        e: MessageEvent<{ result?: string; error?: string; stack?: string }>
      ) => {
        if (e.data.error) {
          const error = new Error(e.data.error);
          if (e.data.stack) error.stack = e.data.stack;
          settle(() => reject(error));
        } else {
          settle(() => resolve(e.data.result ?? ""));
        }
      };

      worker.onerror = (event) => {
        // A worker that fails to load reports a bare Event with no message.
        logger.warn("HTML diff worker failed; diffing on the main thread", {
          operation: "computeHtmlDiff",
          error: event.message || "The diff worker failed to load",
        });
        settle(() => {
          try {
            resolve(executeHtmlDiff(oldHtml, newHtml));
          } catch (err) {
            reject(err);
          }
        });
      };

      worker.postMessage({ oldHtml, newHtml });
    } catch (error) {
      logger.warn(
        "Could not start the HTML diff worker; diffing on the main thread",
        { operation: "computeHtmlDiff", error }
      );
      worker?.terminate();
      // Fall back to synchronous on next tick to yield the call stack
      setTimeout(() => {
        try {
          resolve(executeHtmlDiff(oldHtml, newHtml));
        } catch (err) {
          reject(err);
        }
      }, 0);
    }
  });
};
